<script setup>
import { ref, computed, onMounted } from 'vue'
import CloudinaryImage from '@components/CloudinaryImage.vue'
import CloudinaryMedia from '@components/CloudinaryMedia.vue'
import PhotoGrid from '@components/PhotoGrid.vue'
import Lightbox from '@components/Lightbox.vue'
import LeadGenerationForm from '@components/LeadGenerationForm.vue'
import { useLanguage } from '@composables/useLanguage'
import { useCloudinaryTransform } from '@composables/useCloudinaryTransform'
import { useCloudinary } from '@composables/useCloudinary'
import { useTheme } from '@composables/useTheme'
import { useRoute } from 'vue-router'
import DevTools from '@components/DevTools.vue'

const appName = computed(() => import.meta.env.VITE_APP_NAME || 'SnapKit')
const appVersion = computed(() => import.meta.env.VITE_APP_VERSION || '1.0.0')
const currentYear = new Date().getFullYear()

// Cloudinary transformation composable
const { transformLogo } = useCloudinaryTransform()

// Transform logo URLs with Cloudinary transformations
const appLogo = computed(() => {
  const url = import.meta.env.VITE_APP_LOGO || ''
  return url ? transformLogo(url) : ''
})

const appLogoDark = computed(() => {
  const darkUrl = import.meta.env.VITE_APP_LOGO_DARK
  const lightUrl = import.meta.env.VITE_APP_LOGO

  // If dark logo exists, use it with standard transform
  if (darkUrl) {
    return transformLogo(darkUrl)
  }

  // If only light logo exists, add white background for dark mode visibility
  if (lightUrl) {
    return transformLogo(lightUrl, { addWhiteBackground: true })
  }

  return ''
})

// Language management
const { currentLocale, isGerman, toggleLanguage, getLocalizedLink, t } = useLanguage()

// Cloudinary management (for session gallery mode)
const {
  searchMode,
  sessionImages,
  publicId: resolvedPublicId,
  isSearching,
  resourceType
} = useCloudinary()

// Route for getting publicId
const route = useRoute()

// Get publicId from route query - use resolved publicId from useCloudinary for consistency
// This ensures filename queries are resolved to actual publicIds before checking session storage
const currentPublicId = computed(() => {
  // If we have a resolved publicId from Cloudinary composable, use that
  // This handles both direct publicId and filename resolution
  if (resolvedPublicId.value) {
    return resolvedPublicId.value
  }
  // Fallback to route query (for direct publicId parameter)
  return route.query.publicId || ''
})

// Lead generation state
const leadRequired = computed(() => import.meta.env.VITE_APP_LEAD === 'true')
const leadSubmitted = ref(false)

// Check localStorage and verify with server
const checkLeadSubmitted = async () => {
  try {
    const storedData = localStorage.getItem('lead_submitted')
    const storedLeadId = localStorage.getItem('id_')

    if (!storedData) {
      if (import.meta.env.DEV) {
        console.log('[Lead Check] No stored data found')
      }
      return false
    }

    const data = JSON.parse(storedData)
    if (!data.submitted || !data.expiresAt) {
      if (import.meta.env.DEV) {
        console.warn('[Lead Check] Invalid data structure')
      }
      return false
    }

    const now = Date.now()

    // Log for debugging
    if (import.meta.env.DEV) {
      console.log('[Lead Check] Local validation:', {
        now: new Date(now).toISOString(),
        expiresAt: new Date(data.expiresAt).toISOString(),
        nowMs: now,
        expiresAtMs: data.expiresAt,
        isExpired: now > data.expiresAt,
        isFuture: data.timestamp > now,
        leadId: storedLeadId
      })
    }

    // Reject future timestamps
    if (data.timestamp > now) {
      if (import.meta.env.DEV) {
        console.warn('[Lead Check] Future timestamp detected - clearing data')
      }
      localStorage.removeItem('lead_submitted')
      localStorage.removeItem('id_')
      return false
    }

    // Check expiration
    if (now > data.expiresAt) {
      if (import.meta.env.DEV) {
        console.log('[Lead Check] Data expired - clearing')
      }
      localStorage.removeItem('lead_submitted')
      localStorage.removeItem('id_')
      return false
    }

    // Server-side verification if id_ exists
    if (storedLeadId) {
      const verifyUrl = import.meta.env.VITE_LEAD_VERIFY

      if (verifyUrl) {
        try {
          if (import.meta.env.DEV) {
            console.log('[Lead Check] Verifying with server:', verifyUrl)
          }

          // Call verification endpoint with id_ as query parameter
          const verifyResponse = await fetch(
            `${verifyUrl}?id_=${encodeURIComponent(storedLeadId)}&value=true`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json'
              }
            }
          )

          if (import.meta.env.DEV) {
            console.log('[Lead Check] Verification response status:', verifyResponse.status)
          }

          // If verification fails (not 200), clear data and show form
          if (!verifyResponse.ok) {
            if (import.meta.env.DEV) {
              console.warn('[Lead Check] Server verification failed - clearing data')
            }
            localStorage.removeItem('lead_submitted')
            localStorage.removeItem('id_')
            return false
          }

          if (import.meta.env.DEV) {
            console.log('[Lead Check] Server verification passed ✅')
          }
        } catch (verifyError) {
          if (import.meta.env.DEV) {
            console.error('[Lead Check] Verification request failed:', verifyError)
          }
          // On network error, be lenient - don't clear data, just skip verification
          if (import.meta.env.DEV) {
            console.warn(
              '[Lead Check] Network error during verification - continuing without verification'
            )
          }
          // Fall through to return true (local check passed)
        }
      } else if (import.meta.env.DEV) {
        console.log(
          '[Lead Check] No VITE_LEAD_VERIFY URL configured - skipping server verification'
        )
      }
    } else if (import.meta.env.DEV) {
      console.log('[Lead Check] No id_ found - skipping server verification')
    }

    if (import.meta.env.DEV) {
      console.log('[Lead Check] All checks passed - form should be hidden')
    }

    return true
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('[Lead Check] Error:', error)
    }
    return false
  }
}

// Show lead form logic
const showLeadForm = computed(() => {
  // Don't show form while resolving filename to publicId
  if (isSearching.value && searchMode.value === 'filename') {
    if (import.meta.env.DEV) {
      console.log('[showLeadForm] Waiting for filename resolution')
    }
    return false
  }

  // Don't show form while loading session images
  if (isSearching.value && searchMode.value === 'session') {
    if (import.meta.env.DEV) {
      console.log('[showLeadForm] Waiting for session images to load')
    }
    return false
  }

  // Show form ONLY if lead is required AND user hasn't submitted yet
  // This applies to ALL modes: publicId, filename, AND session
  const shouldShow = leadRequired.value && !leadSubmitted.value

  if (import.meta.env.DEV) {
    console.log('[showLeadForm] Computed:', {
      leadRequired: leadRequired.value,
      leadSubmitted: leadSubmitted.value,
      searchMode: searchMode.value,
      isSearching: isSearching.value,
      shouldShow
    })
  }

  return shouldShow
})

// Initialize lead submitted state on mount
onMounted(async () => {
  // Check lead submission status (now async with server verification)
  leadSubmitted.value = await checkLeadSubmitted()

  // Debug info in development
  if (import.meta.env.DEV) {
    const storedData = localStorage.getItem('lead_submitted')
    let parsedData = null
    try {
      parsedData = storedData ? JSON.parse(storedData) : null
    } catch (e) {
      console.error('Failed to parse stored data:', e)
    }

    console.log('=== Lead Generation Debug ===')
    console.log('VITE_APP_LEAD:', import.meta.env.VITE_APP_LEAD)
    console.log('localStorage data:', parsedData)
    console.log('Current time:', new Date(Date.now()).toISOString())
    console.log('leadSubmitted (from check):', leadSubmitted.value)
    console.log('showLeadForm:', showLeadForm.value)
    console.log('currentPublicId:', currentPublicId.value)
    console.log('searchMode:', searchMode.value)
    console.log('isSearching:', isSearching.value)

    if (parsedData) {
      console.log('Submitted:', new Date(parsedData.timestamp).toISOString())
      console.log('Expires:', new Date(parsedData.expiresAt).toISOString())
      console.log(
        'Hours remaining:',
        ((parsedData.expiresAt - Date.now()) / (60 * 60 * 1000)).toFixed(2)
      )
    }
    console.log('============================')
  }
})

// Get leadId from useLeadGeneration composable
const leadId = computed(() => {
  return localStorage.getItem('id_')
})

// Handle successful lead submission
const handleLeadSuccess = () => {
  leadSubmitted.value = true

  // Debug confirmation
  if (import.meta.env.DEV) {
    console.log('Lead submitted successfully - localStorage updated:', {
      lead_submitted: localStorage.getItem('lead_submitted'),
      id_: localStorage.getItem('id_')
    })
  }
}

// Lightbox state
const lightboxOpen = ref(false)
const selectedImageIndex = ref(0)

// Theme management with new useTheme composable
const { isDarkMode, toggleDarkMode, initializeTheme } = useTheme()

// Initialize theme on mount
onMounted(() => {
  initializeTheme()
})

// Alias for template compatibility
const isDark = isDarkMode
const toggleTheme = toggleDarkMode

// Lightbox management
const openLightbox = index => {
  selectedImageIndex.value = index
  lightboxOpen.value = true
}

const closeLightbox = () => {
  lightboxOpen.value = false
}

const handleIndexChange = newIndex => {
  selectedImageIndex.value = newIndex
}
</script>

<template>
  <div
    class="h-screen flex flex-col bg-linear-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 overflow-hidden transition-colors duration-300"
    style="height: 100dvh"
  >
    <!-- Header -->
    <header
      class="shrink-0 bg-theme-header backdrop-blur-xl border-b border-gray-200/50 dark:border-gray-700/50 shadow-sm"
    >
      <div class="px-4 py-3 flex items-center justify-between max-w-screen-sm mx-auto h-full">
        <!-- Logo -->
        <div class="flex items-center h-full">
          <!-- Dark mode logo -->
          <img
            v-if="isDark && appLogoDark"
            :src="appLogoDark"
            :alt="appName"
            class="h-full max-h-12 w-auto object-contain"
          />
          <!-- Light mode logo -->
          <img
            v-else-if="appLogo"
            :src="appLogo"
            :alt="appName"
            class="h-full max-h-12 w-auto object-contain"
          />
        </div>

        <!-- Controls: Language & Theme Toggle -->
        <div class="flex items-center gap-2">
          <!-- Language Switcher -->
          <button
            @click="toggleLanguage"
            class="w-9 h-9 rounded-lg bg-header-buttons border flex items-center justify-center transition-colors font-semibold text-sm cursor-pointer"
            :aria-label="`Switch to ${isGerman ? 'English' : 'German'}`"
          >
            {{ isGerman ? 'EN' : 'DE' }}
          </button>

          <!-- Theme Toggle -->
          <button
            @click="toggleTheme"
            class="w-9 h-9 rounded-lg bg-header-buttons border flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors cursor-pointer"
            :aria-label="t('header.toggleTheme')"
          >
            <!-- Sun icon (show in dark mode) -->
            <svg
              v-if="isDark"
              class="w-4 h-4 icon-light"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="5" />
              <line x1="12" y1="1" x2="12" y2="3" />
              <line x1="12" y1="21" x2="12" y2="23" />
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
              <line x1="1" y1="12" x2="3" y2="12" />
              <line x1="21" y1="12" x2="23" y2="12" />
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
            </svg>
            <!-- Moon icon (show in light mode) -->
            <svg
              v-else
              class="w-4 h-4 icon-dark"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          </button>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main
      class="flex-1 overflow-y-auto overflow-x-hidden px-4 py-4"
      style="-webkit-overflow-scrolling: touch"
    >
      <div class="max-w-screen-sm mx-auto h-full">
        <!-- Lead Generation Form (shown before image if enabled) -->
        <LeadGenerationForm
          v-if="showLeadForm"
          :publicId="currentPublicId"
          @success="handleLeadSuccess"
        />

        <!-- Content shown after lead submission or if lead not required -->
        <template v-else>
          <!-- Session Gallery Mode -->
          <PhotoGrid
            v-if="searchMode === 'session' && sessionImages.length > 0"
            :images="sessionImages"
            @selectImage="openLightbox"
          />

          <!-- Single Image Mode (publicId or filename) -->
          <!-- Show loading while searching for media type -->
          <div
            v-else-if="searchMode === 'filename' && isSearching"
            class="flex items-center justify-center p-8"
          >
            <div class="text-center">
              <div
                class="w-12 h-12 border-4 border-gray-300 dark:border-white/30 loading-spinner rounded-full animate-spin mx-auto mb-4"
              ></div>
              <p class="text-theme-secondary">{{ t('image.searching') }}</p>
            </div>
          </div>

          <!-- Use CloudinaryMedia for automatic video/image detection -->
          <CloudinaryMedia
            v-else
            :public-id="currentPublicId"
            :resource-type="resourceType"
            alt="SnapKit Media"
          />

          <!-- Lightbox for session images -->
          <Lightbox
            :isOpen="lightboxOpen"
            :images="sessionImages"
            :initialIndex="selectedImageIndex"
            @close="closeLightbox"
            @indexChange="handleIndexChange"
          />
        </template>
      </div>
    </main>

    <!-- Footer -->
    <footer
      class="shrink-0 py-3 px-4 bg-theme-primary-60 backdrop-blur-xl border-t border-theme-default-50"
    >
      <div class="text-[10px] text-center text-gray-600 dark:text-gray-400">
        <!-- Footer Links -->
        <div class="flex justify-center items-center gap-2 mb-2 flex-wrap">
          <a
            :href="`${getLocalizedLink('baseUrl')}${getLocalizedLink('imprint')}`"
            class="link-text transition-colors px-2"
          >
            {{ t('footer.imprint') }}
          </a>
          <span class="text-gray-400 dark:text-gray-600">|</span>
          <a
            :href="`${getLocalizedLink('baseUrl')}${getLocalizedLink('privacy')}`"
            class="link-text transition-colors px-2"
          >
            {{ t('footer.privacy') }}
          </a>
          <span class="text-gray-400 dark:text-gray-600">|</span>
          <a
            :href="`${getLocalizedLink('baseUrl')}${getLocalizedLink('privacyPolicy')}`"
            class="link-text transition-colors px-2"
          >
            {{ t('footer.privacyPolicy') }}
          </a>
        </div>
        <!-- Copyright -->
      <!---  <p class="m-0">© {{ currentYear }} {{ t('footer.poweredBy') }} - v{{ appVersion }}</p> -->
      </div>
    </footer>

    <!-- DevTools (Development Only) -->
    <DevTools />
  </div>
</template>

<style scoped>
/* Header Buttons */
.bg-header-buttons {
  background-color: #e6e6e6;
  border-color: var(--theme-button-secondary-border);
  color: #646464;
}

.bg-theme-header {
  background-color: var(--color-theme-bg-header);
}

.text-theme-secondary {
  color: var(--color-theme-secondary-text);
}

.bg-theme-primary-60 {
  background-color: color-mix(in oklab, var(--color-theme-bg-primary) 60%, transparent);
}

.border-theme-default-50 {
  border-color: color-mix(in srgb, var(--color-theme-border) 50%, transparent);
}

/* Theme Toggle Icon Colors */
.icon-light {
  color: #646464;
}

.icon-dark {
  color: #646464;
}

/* Footer Link Hover */
.link-text:hover {
  color: var(--theme-primary-hover);
}

/* Loading Spinner Border */
.loading-spinner {
  border-top-color: var(--theme-primary-color);
}
</style>
